import { A, useLocation } from "@solidjs/router";
import { AiOutlineHeart } from "solid-icons/ai";
import {
  IoBookmarkOutline,
  IoCardOutline,
  IoChatbubbleEllipsesOutline,
  IoCloseOutline,
  IoGridSharp,
  IoHomeOutline,
  IoMenuOutline,
  IoPeopleOutline,
  IoSearchOutline,
  IoSettingsOutline,
} from "solid-icons/io";
import {
  type Component,
  createMemo,
  For,
  type JSX,
  type ParentComponent,
  Show,
} from "solid-js";
import { useI18n } from "@/api/i18n";
import compactLogo from "@/assets/compact-light-logo.svg";
import extendedLogo from "@/assets/extended-light-logo.svg";
import { CompanySelector } from "@/components/shared/CompanySelector";
import { Button } from "@/components/ui/button";
import { LoadingSkeleton as Skeleton } from "@/components/ui/skeleton";
import { DunningBanner } from "@/domains/billing/components/DunningBanner";
import { isBillingEnabled } from "@/domains/billing/utils/is-billing-enabled";
import { SupportModal } from "@/domains/business/support/components/SupportModal";
import { useCompanyStore } from "@/domains/company/company/store/company-store";
import { Container } from "../shared/Container";
import {
  AppSwitcherPopover,
  DesktopTopBar,
  MainContentContainer,
  MobileOverlay,
  MobileTopBar,
  Sidebar,
  SidebarActionItem,
  SidebarNav,
  SidebarNavigationItem,
  SidebarNavigationTitle,
  SidebarSection,
} from "../shared/Navigation";
import { isFindCandidatesNavActive } from "./company-nav";
import { LayoutProvider, useLayoutContext } from "./context/LayoutContext";

interface SidebarLayoutProps {
  children?: JSX.Element;
}

export const CompanySidebar: ParentComponent<SidebarLayoutProps> = (props) => {
  const {
    isMobileMenuOpen,
    setIsSupportModalOpen,
    toggleMobileMenu,
    handleOpenDonation,
  } = useLayoutContext();
  const location = useLocation();
  const { t } = useI18n();
  const [companyStore] = useCompanyStore();
  const isSoloFreelancerWorkspace = () =>
    companyStore.selectedCompany?.kind === "solo_freelancer";

  const dashboardItem = createMemo(() => ({
    name: t("company.sidebar.dashboard"),
    href: "/profile/company",
    icon: IoHomeOutline,
    active: location.pathname === "/profile/company",
  }));

  const applicantNavigationItems = createMemo(() => {
    return [
      {
        name: t("company.sidebar.search_freelancers"),
        href: "/profile/company/find-candidates",
        icon: IoSearchOutline,
        active: isFindCandidatesNavActive(location.pathname),
      },
      {
        name: t("company.sidebar.talent_pool"),
        href: "/profile/company/talent-pool",
        icon: IoBookmarkOutline,
        active: location.pathname.startsWith("/profile/company/talent-pool"),
      },
      {
        name: t("company.sidebar.ats"),
        href: "/profile/company/ats",
        icon: IoGridSharp,
        active: location.pathname.startsWith("/profile/company/ats"),
      },
    ];
  });

  const companyNavigationItems = createMemo(() => {
    return [
      {
        name: t("company.sidebar.members"),
        href: "/profile/company/members",
        icon: IoPeopleOutline,
        active: location.pathname === "/profile/company/members",
      },
      {
        name: t("company.sidebar.settings"),
        href: "/profile/company/settings",
        icon: IoSettingsOutline,
        active:
          location.pathname === "/profile/company/settings" ||
          (location.pathname.startsWith("/profile/company/settings") &&
            !location.pathname.startsWith("/profile/company/settings/billing")),
      },
      {
        name: t("company.sidebar.billing"),
        href: "/profile/company/settings/billing",
        icon: IoCardOutline,
        active:
          location.pathname.startsWith("/profile/company/settings/billing") ||
          location.pathname.startsWith("/profile/company/upgrade"),
      },
    ].filter(
      (item) =>
        (item.href !== "/profile/company/members" ||
          !isSoloFreelancerWorkspace()) &&
        (item.href !== "/profile/company/settings/billing" ||
          isBillingEnabled()),
    );
  });

  return (
    <div class="min-h-screen bg-gray-50">
      {/* Mobile Header */}
      <MobileTopBar>
        <div class="flex items-center gap-3">
          <img
            src={compactLogo}
            alt="Sevedemo logo"
            class="w-auto h-8 block md:hidden"
          />

          <CompanySelector />
        </div>
        <div class="flex items-center justify-end gap-2">
          <Button
            as={A}
            href="/profile/company/find-candidates"
            variant="default"
            size="sm"
          >
            {t("company.sidebar.search_freelancers")}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={toggleMobileMenu}
            aria-label={
              isMobileMenuOpen()
                ? t("layout.common.closeMenu")
                : t("layout.common.openMenu")
            }
            aria-expanded={isMobileMenuOpen()}
            aria-controls="mobile-menu"
          >
            <Show
              when={isMobileMenuOpen()}
              fallback={<IoMenuOutline class="w-6 h-6" />}
            >
              <IoCloseOutline class="w-6 h-6" />
            </Show>
          </Button>
        </div>
      </MobileTopBar>
      <DesktopTopBar>
        <div class="flex items-center gap-2">
          <img
            src={compactLogo}
            alt="Sevedemo logo"
            class="w-auto h-6 block md:hidden"
          />
          <img
            src={extendedLogo}
            alt="Sevedemo logo"
            class="w-auto h-6 hidden md:block"
          />
        </div>
        <div class="flex items-center gap-2">
          <CompanySelector />
          <Button
            as={A}
            href="/profile/company/find-candidates"
            variant="default"
            size="sm"
          >
            {t("company.sidebar.search_freelancers")}
          </Button>
        </div>
      </DesktopTopBar>

      {/* Mobile Overlay */}
      <MobileOverlay />

      <div class="flex">
        {/* Sidebar */}
        <Sidebar>
          <SidebarNav>
            <SidebarSection>
              <div class="flex flex-col gap-2">
                <SidebarNavigationItem {...dashboardItem()} />
              </div>
            </SidebarSection>
            <SidebarSection>
              <SidebarNavigationTitle>
                {t("company.sidebar.applicant")}
              </SidebarNavigationTitle>
              <div class="flex flex-col gap-2">
                <For each={applicantNavigationItems()}>
                  {(item) => <SidebarNavigationItem {...item} />}
                </For>
              </div>
            </SidebarSection>
            <SidebarSection>
              <SidebarNavigationTitle>
                {t("company.sidebar.company")}
              </SidebarNavigationTitle>
              <div class="flex flex-col gap-2">
                <For each={companyNavigationItems()}>
                  {(item) => <SidebarNavigationItem {...item} />}
                </For>
              </div>
            </SidebarSection>
            <SidebarSection>
              <SidebarNavigationTitle>
                {t("layout.common.helpCenter")}
              </SidebarNavigationTitle>
              <div class="flex flex-col gap-2">
                <SidebarActionItem
                  name={t("layout.common.contactSupport")}
                  icon={IoChatbubbleEllipsesOutline}
                  onClick={() => setIsSupportModalOpen(true)}
                />
                <SidebarActionItem
                  name={t("layout.common.donate")}
                  icon={AiOutlineHeart}
                  onClick={handleOpenDonation}
                />
              </div>
            </SidebarSection>
          </SidebarNav>
          <AppSwitcherPopover type="provider" />
        </Sidebar>

        {/* Main Content */}
        <MainContentContainer>
          <Show when={isBillingEnabled()}>
            <DunningBanner />
          </Show>
          {props.children}
        </MainContentContainer>
      </div>

      {/* Support Modal */}
      <SupportModal />
    </div>
  );
};

export const CompanySidebarLayout: ParentComponent<SidebarLayoutProps> = (
  props,
) => {
  return (
    <LayoutProvider sidebarStorageKey="sidebar_company">
      <CompanySidebar>{props.children}</CompanySidebar>
    </LayoutProvider>
  );
};

export const CompanyLayoutMainContentContainer: ParentComponent = (props) => {
  return (
    <Container class="px-0! lg:px-6!" size="full">
      {props.children}
    </Container>
  );
};

export const CompanyLayoutSkeleton: Component = () => {
  return (
    <LayoutProvider sidebarStorageKey="sidebar_company">
      <div class="min-h-screen bg-gray-50">
        {/* Mobile TopBar Skeleton */}
        <MobileTopBar>
          <div class="flex items-center gap-3">
            <Skeleton width={36} height={36} class="rounded-md" />
            <Skeleton width={120} height={40} class="rounded" />
          </div>
          <div class="flex items-center justify-end">
            <Skeleton width={120} height={36} class="rounded" />
          </div>
        </MobileTopBar>

        {/* Desktop TopBar Skeleton */}
        <DesktopTopBar>
          <div class="flex items-center gap-2">
            <Skeleton width={140} height={40} class="rounded" />
          </div>
          <div class="flex items-center gap-2">
            <Skeleton width={140} height={36} class="rounded" />
          </div>
        </DesktopTopBar>

        <div class="flex">
          {/* Sidebar Skeleton */}
          <Sidebar>
            <SidebarNav>
              {/* Company Section Skeleton */}
              <div class="mt-8">
                <Skeleton width={96} height={16} class="mb-4 rounded" />
                <div class="flex flex-col gap-2">
                  <Skeleton width={240} height={40} class="rounded-lg" />
                  <Skeleton width={240} height={40} class="rounded-lg" />
                </div>
              </div>

              {/* Help Center Section Skeleton */}
              <div class="mt-8">
                <Skeleton width={128} height={16} class="mb-4 rounded" />
                <div class="flex flex-col gap-2">
                  <Skeleton width={240} height={40} class="rounded" />
                  <Skeleton width={240} height={40} class="rounded" />
                </div>
              </div>
            </SidebarNav>
            {/* App Switcher Skeleton */}
            <div class="px-4 py-4 border-t border-gray-200">
              <Skeleton
                width={240}
                height={40}
                class="rounded-lg animate-pulse bg-gray-200"
              />
            </div>
          </Sidebar>

          {/* Main Content Skeleton */}
          <MainContentContainer>
            <Container class="px-0! lg:px-6!" size="full">
              {/* Desktop Content Skeleton */}
              <div class="hidden lg:block w-full space-y-6">
                <Skeleton class="rounded w-full h-10" />
                <Skeleton class="rounded-lg w-full h-96" />
              </div>
            </Container>
          </MainContentContainer>
        </div>
      </div>
    </LayoutProvider>
  );
};
